import React from 'react';
import {
  DatePickerModal,
  formatDatePickerDate,
} from 'QuoteApp/components/DatePickerModal';
import {
  AccidentChallengeFieldsType,
  AccidentChallengeFormValuesType,
} from 'QuoteApp/services/endpoints/PostChallengeAccident';
import { generateFieldDataTestId } from 'QuoteApp/utils/formik';
import { Column, FormInput } from 'Shared/components';
import { getMonthNameFromNumber } from 'Shared/utils/date';
import { FormikErrors } from 'formik';

import styles from './styles.module.css';
import { useAppContext } from 'QuoteApp/services/providers';
import { AccidentDataType } from 'QuoteApp/services/endpoints/GetDetails';
import { getDisplayValue } from '../ReportDetailRow';
import { capitalizeFirstLetter } from 'Policies/utils/helpers';
import { formatMoney } from 'Shared/utils';
import { useQuoteAppFeatureFlags } from 'QuoteApp/utils/hooks';
import { Checkbox } from '@clearcover/atlas';

type AccidentChallengeFormPropsType = {
  accident: AccidentDataType;
  identifier: string;
  setFieldValue: (
    field: string,
    value: any,
    shouldValidate?: boolean | undefined
  ) => void;
  values: AccidentChallengeFormValuesType;
  errors: FormikErrors<AccidentChallengeFormValuesType>;
  isEditing: boolean;
  isLendingLoss?: boolean;
};

export const getInputProps = (
  fieldName: keyof AccidentChallengeFieldsType,
  values: AccidentChallengeFormPropsType['values'],
  identifier: string,
  setFieldValue: AccidentChallengeFormPropsType['setFieldValue'],
  errors: AccidentChallengeFormPropsType['errors']
) => {
  const fullFieldName = `${identifier}.${fieldName}`;
  return {
    name: fullFieldName,
    className: styles.formInput,
    'data-testid': generateFieldDataTestId(fieldName),
    variant: 'selectAutocomplete' as const,
    onChange: (value: string) => {
      setFieldValue(fullFieldName, value);
    },
    value: values[identifier][fieldName],
    errorMessage: errors[identifier]?.[fieldName],
  };
};

export const AccidentChallengeForm = ({
  accident,
  identifier,
  values,
  setFieldValue,
  errors,
  isEditing,
  isLendingLoss,
}: AccidentChallengeFormPropsType) => {
  const { config } = useAppContext();
  const {
    assumeAtFaultFlag,
    collectTypeOfLossFlag,
  } = useQuoteAppFeatureFlags();

  const renderChallengeAccidentForm = () => (
    <>
      <Column variant="quarter">
        <DatePickerModal
          modalHeader="Commission Effective Date"
          formInputClassName={styles.formInput}
          name="effectiveDate"
          startDate={
            values[identifier].date
              ? new Date(values[identifier].date ?? '')
              : undefined
          }
          onDateChange={date => {
            setFieldValue(`${identifier}.day`, date.getDate());
            setFieldValue(
              `${identifier}.month`,
              getMonthNameFromNumber(date.getMonth() + 1).toLocaleLowerCase()
            );
            setFieldValue(`${identifier}.year`, date.getFullYear());
            setFieldValue(`${identifier}.date`, formatDatePickerDate(date));
          }}
        />
      </Column>
      {collectTypeOfLossFlag && (
        <>
          <Column variant="quarter">
            <FormInput
              {...getInputProps(
                'accidentType',
                values,
                identifier,
                setFieldValue,
                errors
              )}
              data-testid="accident-type-field"
              variant="selectAutocomplete"
              options={
                isLendingLoss
                  ? config?.drivers.accidents.nonListedDriverClaimTypes || {}
                  : config?.drivers.accidents.driverClaimTypes || {}
              }
            />
          </Column>
          <Column variant="sixth">
            <FormInput
              {...getInputProps(
                'lossAmount',
                values,
                identifier,
                setFieldValue,
                errors
              )}
              data-testid="accident-type-field"
              variant="text"
              type="number"
              onChange={event => {
                setFieldValue(
                  `${identifier}.lossAmount`,
                  event.currentTarget.value
                );
              }}
            />
          </Column>
        </>
      )}
      {!assumeAtFaultFlag && (
        <Column variant={'third'} className={styles.atFaultCheckboxWrapper}>
          <Checkbox
            {...getInputProps(
              'atFault',
              values,
              identifier,
              setFieldValue,
              errors
            )}
            value={/true/i.test(values[identifier]['atFault'])}
            label="This was an at-fault accident"
            data-testid="accident-at-fault-field"
            onChange={(value: boolean) =>
              setFieldValue(`${identifier}.atFault`, value.toString())
            }
          />
        </Column>
      )}
    </>
  );

  const renderReadOnly = () => (
    <div
      data-testid={`accidents-challenge-read-only-attributes-${accident.identifier}`}
      className={styles.accidentsChalengeReadOnlyAttributesWrapper}
    >
      <Column
        variant="quarter"
        className="fs-mask"
        data-testid="accidents-challenge-date"
      >
        {accident.date.value}
      </Column>
      {collectTypeOfLossFlag && (
        <>
          <Column
            variant="quarter"
            className="fs-mask"
            data-testid="accidents-challenge-accident-type"
          >
            {getDisplayValue(
              capitalizeFirstLetter(accident.accidentType.value || '-')
            )}
          </Column>
          <Column
            variant="sixth"
            className="fs-mask"
            data-testid="accidents-challenge-loss-amount"
          >
            {accident.lossAmount.value
              ? formatMoney(accident.lossAmount.value)
              : '-'}
          </Column>
        </>
      )}
      {!assumeAtFaultFlag && (
        <Column variant="quarter" className="fs-mask">
          {getDisplayValue(values[identifier].atFault)}
        </Column>
      )}
    </div>
  );

  return <>{isEditing ? renderChallengeAccidentForm() : renderReadOnly()}</>;
};
